import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth } from "@/App";
import { cvApi, emptyResume } from "@/lib/cv";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import ResumeRenderer from "@/components/cv/ResumeRenderer";
import {
  FileText, Sparkles, Crown, Star, Zap, Plus, Edit2, Trash2,
  Briefcase, Award, Lock, Clock, ArrowLeft, Loader2, Eye,
  MessageSquare, ShieldCheck, DollarSign, BookOpen, Mail, Wand2,
  CheckCircle2, Globe2, Infinity as InfinityIcon, Languages,
} from "lucide-react";
import "@/components/cv/resume.css";

const CATEGORY_ICONS = {
  professional: Briefcase, modern: Sparkles, executive: Crown,
  ats: ShieldCheck, academic: BookOpen, corporate: Briefcase,
  premium: Star, minimal: FileText, creative: Zap, classic: Award, elegant: Crown,
};

const TOOLS = [
  { key: "builder",   ar: "إنشاء سيرة ذاتية",     en: "Build Resume",        Icon: FileText,     color: "from-yellow-500 to-amber-700",   route: "/cv/new" },
  { key: "wizard",    ar: "ساعدني خطوة بخطوة",   en: "AI Wizard",           Icon: Wand2,        color: "from-fuchsia-500 to-purple-700", route: "/cv/wizard" },
  { key: "cover",     ar: "خطاب تغطية",            en: "Cover Letter",        Icon: Mail,         color: "from-purple-500 to-violet-700",  route: "/cv/tools/cover-letter" },
  { key: "ats",       ar: "تحسين ATS",             en: "ATS Optimization",    Icon: ShieldCheck,  color: "from-emerald-500 to-teal-700",   route: "/cv/tools/ats" },
  { key: "interview", ar: "تحضير المقابلات",        en: "Interview Prep",      Icon: MessageSquare,color: "from-blue-500 to-cyan-700",      route: "/cv/tools/interview" },
  { key: "salary",    ar: "دليل الرواتب",          en: "Salary Guide",        Icon: DollarSign,   color: "from-green-500 to-emerald-700",  route: "/cv/tools/salary" },
  { key: "advice",    ar: "نصائح المسار المهني",   en: "Career Advice",       Icon: BookOpen,     color: "from-rose-500 to-pink-700",      route: "/cv/tools/advice" },
];

export default function CVCenter() {
  const { t, language, toggleLanguage } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tplData, setTplData] = useState(null);
  const [sample,  setSample]  = useState(null);
  const [pricing, setPricing] = useState(null);
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [tpl, smp, pr] = await Promise.all([
          cvApi.templates().catch(() => null),
          cvApi.sample("en").catch(() => null),
          cvApi.pricing().catch(() => null),
        ]);
        if (!mounted) return;
        if (tpl) setTplData(tpl);
        if (smp) setSample(smp);
        if (pr)  setPricing(pr);
        if (token) {
          const r = await cvApi.list(token).catch(() => []);
          if (mounted) setResumes(r);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const newResume = async () => {
    if (!token) { navigate("/login"); return; }
    try {
      const r = await cvApi.create(emptyResume(), token);
      navigate(`/cv/edit/${r.resume_id}`);
    } catch { toast.error(t("تعذّر إنشاء سيرة جديدة", "Could not create resume")); }
  };

  const startWizard = () => {
    if (!token) { navigate("/login"); return; }
    navigate("/cv/wizard");
  };

  const delResume = async (id) => {
    if (!window.confirm(t("هل تريد حذف هذه السيرة؟", "Delete this resume?"))) return;
    try {
      await cvApi.remove(id, token);
      setResumes((rs) => rs.filter(r => r.resume_id !== id));
      toast.success(t("تم الحذف", "Deleted"));
    } catch { toast.error(t("فشل الحذف", "Delete failed")); }
  };

  // Hardcoded fallback sample so the hero preview is never an empty white box even if
  // the /cv/sample API call is slow or fails. Used only when `sample` hasn't loaded yet.
  const HERO_FALLBACK_SAMPLE = {
    contact: {
      full_name: "Mony Spider",
      title: "Software Engineer",
      email: "mony.spider@email.com",
      phone: "(415) 555-0123",
      location: "San Francisco, CA",
      linkedin: "linkedin.com/in/monyspider",
      website: "",
    },
    summary:
      "Full-stack software engineer with 7+ years building scalable web and mobile products at high-growth US startups. Proven track record shipping React, Node.js, and AWS-backed services that serve millions of monthly users. Passionate about clean architecture and developer experience.",
    experience: [
      { id: "ex1", role: "Senior Software Engineer", company: "Northwave Labs", location: "San Francisco, CA",
        start_month: "2022-03", end_month: "Present",
        bullets: [
          "Led migration of legacy monolith to micro-services architecture, cutting API latency by 38%.",
          "Mentored 4 mid-level engineers; team velocity rose 25% over two quarters.",
          "Designed and shipped a real-time notifications platform handling 4M+ daily events.",
        ] },
      { id: "ex2", role: "Software Engineer", company: "Brightline", location: "Austin, TX",
        start_month: "2019-06", end_month: "2022-02",
        bullets: [
          "Built React + TypeScript dashboard used daily by 12k SMB customers.",
          "Owned end-to-end checkout flow; conversion improved 17% YoY.",
          "Introduced automated E2E test suite covering 92% of critical paths.",
        ] },
    ],
    education: [
      { id: "ed1", degree: "B.S., Computer Science", school: "UC Berkeley",
        start_month: "2014-09", end_month: "2018-06" },
    ],
    skills: ["React", "TypeScript", "Node.js", "Python", "AWS", "PostgreSQL", "Docker", "Kubernetes"],
    achievements: [
      "AWS Certified Solutions Architect — Associate (2023)",
      "Open-source contributor — Next.js, React Query",
    ],
  };
  // The hero preview always uses the same hardcoded "Mony Spider / Software Engineer"
  // showcase resume so the marketing page reads the same regardless of the API state.
  const heroSample = HERO_FALLBACK_SAMPLE;
  // Build the flat list of templates with category metadata for the previews row.
  const allTemplates = tplData
    ? [...tplData.free, ...tplData.categories.flatMap(c => c.templates.filter(x => x.premium))]
    : [];
  const featuredTemplates = allTemplates.slice(0, 9);
  const regular = pricing?.regular_price_usd ?? 13.99;
  const offer   = pricing?.offer_price_usd ?? 4.99;
  const saving  = pricing?.savings_pct ?? Math.round((1 - offer / regular) * 100);

  return (
    <DashboardLayout title={t("مركز السيرة الذاتية", "Resume Center")}>
      <div className="space-y-8" data-testid="cv-center">

        {/* HERO with dual CTAs */}
        <div className="relative glass-card rounded-3xl overflow-hidden p-6 lg:p-10 bg-luxury">
          <div className="absolute -top-40 -right-40 w-[480px] h-[480px] bg-yellow-500/20 blur-[140px] rounded-full" />
          <div className="absolute -bottom-40 -left-40 w-[480px] h-[480px] bg-purple-500/20 blur-[140px] rounded-full" />
          <div className="relative grid md:grid-cols-[1.4fr_1fr] gap-6 items-end">
            <div>
              <div className="flex items-center justify-between gap-2 mb-3">
                <Badge className="bg-gradient-to-r from-purple-500/20 to-yellow-500/20 text-yellow-300 border-yellow-500/30">
                  <Crown size={12} className="ml-1" /> {t("مدرّب مهني احترافي", "Premium Career Coach")}
                </Badge>
                {/* Inline language toggle — switches the whole UI between Arabic & English. */}
                <button
                  onClick={toggleLanguage}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10 transition"
                  data-testid="cv-lang-toggle"
                  aria-label="Switch language"
                >
                  <Languages size={12} />
                  {language === "ar" ? "English" : "العربية"}
                </button>
              </div>
              <h2 className="text-3xl lg:text-5xl font-bold mb-3 leading-tight text-luxury">
                {t("ابنِ مستقبلك في أمريكا", "Build your future in America")}
              </h2>
              <p className="text-slate-300 max-w-2xl text-sm lg:text-base">
                {t(
                  "اكتب سيرتك بالعربية، وسنحوّلها إلى إنجليزية أمريكية احترافية. مدرّب ذكي يسألك خطوة بخطوة، يراجع سيرتك قبل التحميل، ويعطيك تقريراً عن جودتها وتوافقها مع أنظمة ATS.",
                  "Write your resume in Arabic — we'll translate it into polished US business English. An AI coach walks you through step-by-step, reviews your resume before download, and grades its ATS compatibility."
                )}
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                <Button onClick={startWizard} className="btn-primary px-5 py-5 text-sm" data-testid="cv-start-wizard-btn">
                  <Wand2 size={16} className="ml-2" />{t("ابدأ بمساعدة الذكاء الاصطناعي", "Start with AI Wizard")}
                </Button>
                <Button onClick={newResume} variant="outline" className="border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10 px-5 py-5 text-sm" data-testid="cv-new-resume-btn">
                  <Edit2 size={14} className="ml-2" />{t("ابدأ يدوياً وحسّن بالذكاء الاصطناعي", "Start manually & refine with AI")}
                </Button>
              </div>
              <div className="flex flex-wrap gap-3 mt-4 text-[11px] text-slate-400">
                <span className="inline-flex items-center gap-1"><Languages size={12} className="text-yellow-400" />{t("عربي/إنجليزي تلقائي", "AR / US English auto-translate")}</span>
                <span className="inline-flex items-center gap-1"><ShieldCheck size={12} className="text-emerald-400" />{t("متوافق مع ATS", "ATS-friendly")}</span>
                <span className="inline-flex items-center gap-1"><Sparkles size={12} className="text-fuchsia-400" />{t("مراجعة جودة قبل التحميل", "AI quality review before download")}</span>
              </div>
            </div>
            {/* Inline mini-preview of a sample resume — same component, same data in both languages.
                The wrapper is wrapped in an LTR scope so the ResumeRenderer's left-to-right layout
                renders identically in both Arabic and English. Visible on mobile too as a thumbnail. */}
            <div className="relative mx-auto md:mx-0 w-full max-w-[260px] sm:max-w-[300px] md:max-w-[340px] lg:max-w-[380px]" dir="ltr" data-testid="cv-hero-preview">
              <div className="absolute -inset-3 md:-inset-4 bg-yellow-500/20 blur-3xl rounded-3xl" />
              <div className="relative bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-2xl shadow-black/60 border border-yellow-500/30 aspect-[3/4]">
                <div className="preview-frame-compact w-full h-full" style={{ background: "transparent", padding: 0, overflow: "hidden", pointerEvents: "none" }}>
                  <ResumeRenderer template="professional" data={heroSample} allTemplates={allTemplates} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CAREER SUCCESS PACKAGE — Premium offer ($4.99 + Lifetime) */}
        <div className="relative glass-card rounded-3xl overflow-hidden p-6 lg:p-8 border-2 border-yellow-500/40">
          <div className="absolute -top-20 right-10 w-72 h-72 bg-yellow-500/15 blur-[120px] rounded-full" />
          <div className="relative grid lg:grid-cols-3 gap-5 items-center">
            <div className="lg:col-span-2 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold">⭐⭐⭐⭐⭐ {t("الأكثر شعبية", "Most Popular")}</Badge>
                <Badge className="bg-red-500/90 text-white">🔥 {t("عرض محدود", "Limited-Time Offer")}</Badge>
                <Badge className="bg-emerald-500/90 text-black font-bold">-{saving}%</Badge>
              </div>
              <h3 className="text-2xl lg:text-3xl font-bold text-white">🚀 {t("باقة النجاح المهني", "Career Success Package")}</h3>
              <p className="text-sm text-slate-300 italic">{t("أنت لا تشتري ملف PDF — أنت تستثمر في فرصة أفضل للحصول على وظيفة في أمريكا.", "You are not buying a PDF — you are investing in a better chance at landing the job in America.")}</p>
              <p className="text-sm font-semibold text-yellow-300 pt-2">{t("ماذا تحصل عليه:", "Everything you get:")}</p>
              <div className="space-y-2.5 text-slate-200">
                <div data-testid="benefit-templates">
                  <p className="text-sm font-semibold inline-flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />{t("✅ +50 قالب احترافي بتصاميم حقيقية مختلفة", "✅ 50+ Unique Resume Templates")}</p>
                  <p className="text-xs text-slate-400 pr-6 leading-relaxed">{t(
                    "اختر من بين أكثر من 50 تصميم مختلف فعلياً — وليس نفس التصميم بألوان مختلفة. مع دعم الصورة الشخصية و10 ألوان متعددة لتختار ما يناسب مجالك.",
                    "Choose from 50+ genuinely different layouts — not the same design in different colors. With profile picture support and 10 color themes to match your field.")}</p>
                </div>
                <div data-testid="benefit-wizard">
                  <p className="text-sm font-semibold inline-flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />{t("✅ مدرّب مهني بالذكاء الاصطناعي", "✅ AI Career Coach")}</p>
                  <p className="text-xs text-slate-400 pr-6 leading-relaxed">{t(
                    "مساعد ذكي يبني سيرتك خطوة بخطوة، يحسّن طريقة وصف خبراتك، يكتشف المهارات المهمة في مجالك، ويقترح صياغة أقوى — مثل وجود خبير توظيف بجانبك.",
                    "A personal AI assistant that builds your resume step-by-step, improves your experience descriptions, identifies important skills, and suggests stronger wording — like having a hiring expert by your side.")}</p>
                </div>
                <div data-testid="benefit-translation">
                  <p className="text-sm font-semibold inline-flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />{t("✅ ترجمة إنجليزية أمريكية احترافية", "✅ Professional American Translation")}</p>
                  <p className="text-xs text-slate-400 pr-6 leading-relaxed">{t(
                    "نُعيد كتابة سيرتك بلغة الأعمال الأمريكية الحقيقية — وليس ترجمة حرفية. صياغة يفهمها أصحاب العمل الأمريكيون ويتفاعلون معها.",
                    "We rewrite your resume in real US business English — not a literal translation. Wording that American employers understand and respond to.")}</p>
                </div>
                <div data-testid="benefit-ats">
                  <p className="text-sm font-semibold inline-flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />{t("✅ توافق مع أنظمة ATS", "✅ ATS Resume Optimization")}</p>
                  <p className="text-xs text-slate-400 pr-6 leading-relaxed">{t(
                    "اجتز أنظمة الفرز التلقائي التي تستخدمها معظم الشركات الأمريكية لتصفية السير قبل وصولها للموظف البشري — لتزيد فرصتك في أن يراها صاحب العمل فعلاً.",
                    "Pass the automated screening systems most US companies use to filter resumes before a human ever sees them — so your resume actually reaches recruiters.")}</p>
                </div>
                <div data-testid="benefit-review">
                  <p className="text-sm font-semibold inline-flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />{t("✅ مراجعة شاملة قبل التحميل", "✅ Pre-Download Resume Review")}</p>
                  <p className="text-xs text-slate-400 pr-6 leading-relaxed">{t(
                    "تقرير واضح بدرجة احترافية سيرتك من 100، يوضح نقاط القوة وما يجب تحسينه قبل تنزيل PDF — حتى لا ترسل سيرة فيها ضعف دون أن تدري.",
                    "A clear report with a 0–100 quality score showing your resume's strengths and exactly what to fix before you download the PDF — so you never send a weak resume by mistake.")}</p>
                </div>
                <div data-testid="benefit-cover">
                  <p className="text-sm font-semibold inline-flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />{t("✅ خطاب تغطية احترافي", "✅ Professional Cover Letter")}</p>
                  <p className="text-xs text-slate-400 pr-6 leading-relaxed">{t(
                    "خطاب يقدّمك بأسلوب احترافي يصنع انطباعاً قوياً من أول رسالة — يرفع فرصك في الحصول على مقابلة عمل بدلاً من تجاهل طلبك.",
                    "A letter that introduces you professionally and makes a strong first impression — boosting your chance of getting an interview instead of being ignored.")}</p>
                </div>
                <div data-testid="benefit-extras">
                  <p className="text-sm font-semibold inline-flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />{t("✅ تحضير المقابلات + دليل الرواتب", "✅ Interview Prep + Salary Guide")}</p>
                  <p className="text-xs text-slate-400 pr-6 leading-relaxed">{t(
                    "أسئلة المقابلات الشائعة في مهنتك مع نصائح ذكية للإجابة، ومعرفة الراتب المتوسط حسب الولاية حتى تتفاوض من موقع قوة.",
                    "The most common interview questions for your role with smart answer tips, plus median salary by US state so you negotiate from a position of strength.")}</p>
                </div>
                <div data-testid="benefit-lifetime">
                  <p className="text-sm font-semibold inline-flex items-start gap-2"><InfinityIcon size={14} className="text-yellow-400 mt-0.5 flex-shrink-0" />{t("♾ وصول مدى الحياة — بدون اشتراك", "♾ Lifetime Access — No Subscription")}</p>
                  <p className="text-xs text-slate-400 pr-6 leading-relaxed">{t(
                    "ادفع مرة واحدة فقط واستخدمها مدى الحياة — حدّث سيرتك وتنزّلها كلما تقدمت لوظيفة جديدة، بدون رسوم شهرية أو تجديد سنوي.",
                    "Pay once and use it for life — update and re-download your resume every time you apply for a new job, with no monthly fees or yearly renewals.")}</p>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="text-center bg-slate-900/60 rounded-2xl p-5 border border-yellow-500/30">
                {/* Promotional period: show only the current offer price ($4.99). */}
                <p className="text-5xl font-bold text-yellow-400 my-1" data-testid="cv-offer-price">${offer.toFixed(2)}</p>
                <p className="text-xs text-emerald-300 mb-1 inline-flex items-center gap-1 justify-center">🔥 {t("عرض محدود", "Limited-Time Offer")}</p>
                <p className="text-xs text-slate-300 mb-1">{t("دفعة واحدة فقط — مدى الحياة", "One-time payment — Lifetime access")}</p>
                <p className="text-[11px] text-emerald-300 mb-3 inline-flex items-center gap-1 justify-center"><ShieldCheck size={11} /> {t("بدون اشتراك ، بدون تجديد", "No subscription · No renewal")}</p>
                <Button onClick={() => navigate("/cv/premium")} className="w-full btn-primary" data-testid="cv-purchase-btn">
                  <Crown size={14} className="ml-2" />{t("احصل عليها الآن", "Get it now")}
                </Button>
              </div>
              <button
                onClick={() => { localStorage.setItem("cv_preview_all", "1"); toast.success(t("تم فتح كل القوالب للمعاينة", "All templates unlocked for preview")); newResume(); }}
                className="w-full text-xs bg-slate-800/60 hover:bg-slate-800 text-slate-200 rounded-xl py-2.5 px-3 inline-flex items-center justify-center gap-1.5 border border-emerald-500/30"
                data-testid="cv-owner-preview-btn">
                <Eye size={13} className="text-emerald-400" />
                {t("معاينة كل القوالب (للمالك)", "Preview all templates (Owner)")}
              </button>
            </div>
          </div>
        </div>

        {/* FEATURED REAL PREVIEWS */}
        {sample && featuredTemplates.length > 0 && (
          <div>
            <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <Eye className="text-yellow-500" size={20} /> {t("معاينات حقيقية للقوالب", "Real Template Previews")}
              <span className="text-xs text-slate-400 font-normal">({allTemplates.length}+ {t("قالب", "templates")})</span>
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="cv-featured-previews">
              {featuredTemplates.map((tpl) => (
                <button key={tpl.slug}
                  onClick={() => navigate("/cv/wizard")}
                  className="group relative rounded-xl overflow-hidden border border-slate-700 hover:border-yellow-500 transition bg-white"
                  data-testid={`cv-featured-${tpl.slug}`}
                  title={language === "ar" ? tpl.name_ar : tpl.name_en}
                >
                  <div className="preview-frame-compact" style={{ background: "transparent", padding: 0, height: 220, overflow: "hidden", pointerEvents: "none" }}>
                    <ResumeRenderer template={tpl.slug} data={sample} allTemplates={allTemplates} />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent text-white text-[11px] px-2 py-2 flex items-center justify-between">
                    <span className="truncate font-semibold">{language === "ar" ? tpl.name_ar : tpl.name_en}</span>
                    {tpl.premium && <Crown size={11} className="text-yellow-400 flex-shrink-0" />}
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-3 text-center">
              <Button onClick={startWizard} variant="outline" className="border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10">
                <Globe2 size={14} className="ml-2" />{t("اعرض كل القوالب وابدأ", "Browse all templates & start")}
              </Button>
            </div>
          </div>
        )}

        {/* TOOLS GRID */}
        <div>
          <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <Sparkles className="text-yellow-500" size={20} /> {t("أدوات مركز السيرة", "Resume Center Tools")}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="cv-tools-grid">
            {TOOLS.map((tool) => (
              <button
                key={tool.key}
                onClick={() => {
                  if (tool.key === "builder") return newResume();
                  if (tool.key === "wizard")  return startWizard();
                  navigate(tool.route);
                }}
                className="glass-card rounded-2xl p-4 text-center hover:scale-105 transition-all border border-transparent hover:border-yellow-500/40"
                data-testid={`cv-tool-${tool.key}`}
              >
                <div className={`w-12 h-12 mx-auto mb-2 rounded-xl flex items-center justify-center bg-gradient-to-br ${tool.color}`}>
                  <tool.Icon className="text-white" size={22} />
                </div>
                <p className="text-sm font-semibold text-white">{language === "ar" ? tool.ar : tool.en}</p>
              </button>
            ))}
          </div>
        </div>

        {/* MY RESUMES */}
        {token && (
          <div>
            <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <FileText className="text-purple-400" size={20} /> {t("سيري الذاتية", "My Resumes")}
              <span className="text-sm text-slate-400 font-normal">({resumes.length})</span>
            </h3>
            {loading ? (
              <div className="glass-card rounded-2xl p-8 flex items-center justify-center">
                <Loader2 className="animate-spin text-yellow-500" />
              </div>
            ) : resumes.length === 0 ? (
              <EmptyState
                icon={FileText}
                title_ar="لم تنشئ أي سيرة ذاتية بعد"
                title_en="No resumes yet"
                body_ar="ابدأ بمساعدة الذكاء الاصطناعي خطوة بخطوة، أو أنشئها بنفسك من قالب جاهز."
                body_en="Start with our step-by-step AI Wizard, or build one yourself from a ready template."
                cta_ar="ابدأ بالمساعد الذكي"
                cta_en="Start with AI Wizard"
                onCta={startWizard}
                secondaryCtaAr="أنشئ يدوياً"
                secondaryCtaEn="Create manually"
                onSecondary={newResume}
                testid="cv-no-resumes"
              />
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {resumes.map((r) => (
                  <div key={r.resume_id} className="glass-card rounded-2xl p-4 flex flex-col gap-2" data-testid={`cv-resume-${r.resume_id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-white truncate">{r.title || t("بدون عنوان", "Untitled")}</h4>
                        <p className="text-xs text-slate-500">{r.profession || "—"}</p>
                      </div>
                      <Badge className="bg-slate-800 text-slate-300 text-[10px]">{r.template}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <Clock size={10} />
                      {new Date(r.updated_at).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US")}
                    </p>
                    <div className="flex gap-2 pt-2 mt-auto">
                      <Button onClick={() => navigate(`/cv/edit/${r.resume_id}`)} size="sm" className="flex-1 btn-primary">
                        <Edit2 size={12} className="ml-1" />{t("تعديل", "Edit")}
                      </Button>
                      <button onClick={() => delResume(r.resume_id)}
                        className="px-3 py-1.5 rounded bg-slate-800 text-slate-300 hover:bg-red-500/20 hover:text-red-300 transition" data-testid={`cv-delete-${r.resume_id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TEMPLATE CATEGORIES */}
        {tplData && (
          <div>
            <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <Crown className="text-yellow-500" size={20} /> {t("فئات القوالب", "Template Categories")}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cv-categories">
              {tplData.categories.map((cat) => {
                const Icon = CATEGORY_ICONS[cat.slug] || FileText;
                const freeCount = cat.templates.filter(x => !x.premium).length;
                const premiumCount = cat.templates.filter(x => x.premium).length;
                return (
                  <div key={cat.slug} className="glass-card rounded-2xl p-5 hover:border-yellow-500/40 transition-all border border-transparent" data-testid={`cv-cat-${cat.slug}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
                        <Icon className="text-yellow-500" size={20} />
                      </div>
                      {freeCount > 0 && <Badge className="bg-emerald-500/15 text-emerald-300 text-[10px]">{freeCount} {t("مجاني", "free")}</Badge>}
                    </div>
                    <h4 className="font-bold text-white">{language === "ar" ? cat.name_ar : cat.name_en}</h4>
                    <p className="text-xs text-slate-400 mt-1 mb-3">{language === "ar" ? cat.description_ar : cat.description_en}</p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-slate-500">{cat.templates.length} {t("قوالب", "templates")}</span>
                      {premiumCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-yellow-400">
                          <Lock size={10} />{premiumCount} {t("بريميوم", "premium")}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bottom CTA */}
        <div className="glass-card rounded-2xl p-6 text-center">
          <p className="text-slate-300 mb-3">
            {t(
              "✨ سيرة احترافية بمعايير أمريكية بدون صورة، بدون عمر، بدون معلومات شخصية — يقبلها أصحاب العمل وأنظمة ATS.",
              "✨ Professional US-standard resume — no photo, no age, no personal info — accepted by employers and ATS systems.",
            )}
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            <Button onClick={startWizard} className="btn-primary px-6">
              <Wand2 className="ml-2" size={16} />{t("ابدأ بمساعدة الذكاء الاصطناعي", "Start with AI Wizard")}
            </Button>
            <Button onClick={newResume} variant="outline" className="border-slate-700 text-slate-300">
              <Plus className="ml-2" size={14} />{t("ابدأ يدوياً", "Start manually")}
            </Button>
            {!token && (
              <Button onClick={() => navigate("/login")} variant="outline" className="ml-2 border-slate-700 text-slate-300">
                <ArrowLeft className="ml-2" size={14} />{t("سجّل دخول للمتابعة", "Sign in to continue")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
